import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { cx } from '../cx'
import { EmptyState } from '../Feedback'
import './charts.css'

/* Strumenti condivisi dai grafici SVG: misura, animazione, scale, formati italiani, tooltip, tabella equivalente. */

export type ChartColor = 'primary' | 'secondary' | 'tertiary' | 'success' | 'warning' | 'error' | 'neutral'

export const PALETTE: ChartColor[] = ['primary', 'tertiary', 'secondary', 'success', 'warning', 'error']

export function colorVar(c: ChartColor | undefined, i: number): string {
  const name = c ?? PALETTE[i % PALETTE.length]!
  return name === 'neutral' ? 'hsl(var(--md-on-surface))' : `hsl(var(--md-${name}))`
}

export type ChartUnit = 'number' | 'eur' | 'percent'

/** Numeri italiani: punto sulle migliaia sempre (anche 1.234), virgola sui decimali. */
export function formatChartValue(n: number | null | undefined, unit: ChartUnit = 'number', decimals?: number): string {
  if (n == null || !Number.isFinite(n)) return '—'
  const d = decimals ?? (unit === 'eur' ? 2 : Number.isInteger(n) ? 0 : 1)
  const s = n.toLocaleString('it-IT', { minimumFractionDigits: d, maximumFractionDigits: d, useGrouping: 'always' })
  return unit === 'eur' ? `€ ${s}` : unit === 'percent' ? `${s}%` : s
}

export interface ChartSeries {
  id: string
  name: string
  values: Array<number | null>
  color?: ChartColor | undefined
}

export function reducedMotion(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
}

/** Larghezza del contenitore, aggiornata con ResizeObserver. */
export function useWidth(ref: RefObject<HTMLElement | null>): number {
  const [w, setW] = useState(0)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    setW(Math.floor(el.getBoundingClientRect().width))
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(es => {
      const next = Math.floor(es[0]?.contentRect.width ?? 0)
      setW(prev => (prev === next ? prev : next))
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [ref])
  return w
}

/* cubic-bezier(.05,.7,.1,1) = emphasized-decelerate */
function bezier(x1: number, y1: number, x2: number, y2: number) {
  const cx_ = 3 * x1, bx = 3 * (x2 - x1) - cx_, ax = 1 - cx_ - bx
  const cy_ = 3 * y1, by = 3 * (y2 - y1) - cy_, ay = 1 - cy_ - by
  const X = (t: number) => ((ax * t + bx) * t + cx_) * t
  const Y = (t: number) => ((ay * t + by) * t + cy_) * t
  return (x: number) => {
    let lo = 0, hi = 1, t = x
    for (let i = 0; i < 24; i++) { t = (lo + hi) / 2; if (X(t) < x) lo = t; else hi = t }
    return Y(t)
  }
}
const decelerate = bezier(0.05, 0.7, 0.1, 1)
export const CHART_DURATION = 600

/**
 * Valori che scorrono da quelli mostrati ora ai nuovi (all'arrivo partono da 0). Ogni valore ha una chiave stabile:
 * quelli nuovi partono da 0. Con prefers-reduced-motion cambiano subito.
 */
export function useTweened(keys: string[], values: number[], start?: Array<number | undefined>): number[] {
  const shown = useRef(new Map<string, number>())
  const [, tick] = useState(0)
  const sig = keys.join('\u0001') + '\u0002' + values.join(',')
  const targetRef = useRef({ keys, values })
  targetRef.current = { keys, values }
  const startRef = useRef(start)
  startRef.current = start
  useEffect(() => {
    const { keys: ks, values: vs } = targetRef.current
    const from = ks.map((k, i) => shown.current.get(k) ?? startRef.current?.[i] ?? 0)
    if (reducedMotion()) {
      shown.current = new Map(ks.map((k, i) => [k, vs[i]!]))
      tick(n => n + 1)
      return
    }
    let raf = 0
    const t0 = performance.now()
    const step = (now: number) => {
      const t = Math.min(1, (now - t0) / CHART_DURATION)
      const e = decelerate(t)
      shown.current = new Map(ks.map((k, i) => [k, from[i]! + (vs[i]! - from[i]!) * e]))
      tick(n => n + 1)
      if (t < 1) raf = requestAnimationFrame(step)
    }
    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
  }, [sig])
  // Nel primo render (prima dell'effetto) i valori sono 0, non il bersaglio: niente lampo del grafico finito.
  return keys.map((k, i) => shown.current.get(k) ?? start?.[i] ?? 0)
}

/**
 * Estremi della scala che scorrono insieme ai valori: senza questo, durante l'animazione i valori vecchi venivano
 * disegnati sulla scala nuova e potevano uscire dal grafico (barre alte quanto la pagina).
 */
export function useTweenedDomain(min: number, max: number): { min: number; max: number } {
  const [a, b] = useTweened(['§min', '§max'], [min, max], [min, max])
  const lo = a ?? min, hi = b ?? max
  return hi - lo > 1e-9 ? { min: lo, max: hi } : { min, max }
}

/** Scala "bella": estremi e tacche tonde che includono lo zero. */
export function niceScale(min: number, max: number, count = 5): { min: number; max: number; ticks: number[] } {
  let lo = Math.min(0, min), hi = Math.max(0, max)
  if (hi === lo) hi = lo + 1
  const raw = (hi - lo) / Math.max(1, count)
  const mag = Math.pow(10, Math.floor(Math.log10(raw)))
  const norm = raw / mag
  const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10) * mag
  lo = Math.floor(lo / step) * step
  hi = Math.ceil(hi / step) * step
  const ticks: number[] = []
  for (let v = lo; v <= hi + step / 2; v += step) ticks.push(Math.abs(v) < step / 1e6 ? 0 : Number(v.toFixed(10)))
  return { min: lo, max: hi, ticks }
}

/** Indici delle etichette da mostrare quando lo spazio non basta (sempre il primo, poi ogni `step`). */
export function thinIndices(n: number, slot: number, labelWidth: number): Set<number> {
  const step = Math.max(1, Math.ceil((labelWidth + 8) / Math.max(1, slot)))
  const out = new Set<number>()
  for (let i = 0; i < n; i += step) out.add(i)
  return out
}

export const textWidth = (s: string) => s.length * 6.4 + 4
export const clip = (s: string, max: number) => (s.length > max ? s.slice(0, Math.max(1, max - 1)) + '…' : s)

/* ── Contesto con ChartCard (tabella visibile) ── */
export const ChartCardContext = createContext<{ showTable: boolean }>({ showTable: false })

export interface ChartTableData {
  caption: string
  head: string[]
  rows: Array<{ key: string; cells: string[] }>
}

export function ChartTable({ data }: { data: ChartTableData }) {
  const { showTable } = useContext(ChartCardContext)
  return (
    <div className={cx('md-chart__table', !showTable && 'md-sr-only')}>
      <table className="md-table">
        <caption className="md-sr-only">{data.caption}</caption>
        <thead>
          <tr>{data.head.map((h, i) => <th key={i} scope="col" className={i > 0 ? 'md-num' : undefined}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {data.rows.map(r => (
            <tr key={r.key}>{r.cells.map((c, i) => (i === 0 ? <th key={i} scope="row">{c}</th> : <td key={i} className="md-num">{c}</td>))}</tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export interface TooltipRow { label: string; value: string; color?: string | undefined }

export function ChartTooltip({ x, y, width, title, rows }: { x: number; y: number; width: number; title: string; rows: TooltipRow[] }) {
  // Se non c'e' spazio sopra il punto, il tooltip va sotto.
  const below = y < 84
  const ref = useRef<HTMLDivElement>(null)
  const [w, setW] = useState(0)
  useLayoutEffect(() => { setW(ref.current?.offsetWidth ?? 0) }, [title, rows.length])
  const left = Math.max(0, Math.min(width - w, x - w / 2))
  return (
    <div ref={ref} className="md-chart__tooltip" role="presentation" style={below ? { left, top: y + 14, transform: 'none' } : { left, top: y - 10 }}>
      <div className="md-chart__tooltip-title">{title}</div>
      {rows.map((r, i) => (
        <div key={i} className="md-chart__tooltip-row">
          {r.color ? <span className="md-chart__swatch" style={{ background: r.color }} aria-hidden="true" /> : null}
          <span className="md-chart__tooltip-label">{r.label}</span>
          <span className="md-num">{r.value}</span>
        </div>
      ))}
    </div>
  )
}

export interface ChartShellProps {
  loading?: boolean | undefined
  empty?: boolean | undefined
  emptyTitle?: string | undefined
  emptyDescription?: string | undefined
  ariaLabel: string
  table: ChartTableData
  height: number
  /** L'altezza e' un minimo (contenuto HTML a misura). */
  autoHeight?: boolean | undefined
  className?: string | undefined
  /** Mappa: riceve la larghezza misurata (>0) e restituisce il contenuto SVG/HTML. */
  children: (width: number) => ReactNode
  /** Navigazione da tastiera: frecce, Home, End, Esc. */
  count: number
  active: number | null
  onActive: (i: number | null) => void
  /** true per i grafici che scorrono in verticale (barre orizzontali, funnel): Su/Giù invece di Sinistra/Destra. */
  vertical?: boolean | undefined
  announce: string
  /** Tooltip da mostrare nel contenitore (coordinate relative). */
  tooltip?: ReactNode
  onLeave?: (() => void) | undefined
}

export function ChartShell(p: ChartShellProps) {
  const { showTable } = useContext(ChartCardContext)
  const ref = useRef<HTMLDivElement>(null)
  const width = useWidth(ref)
  const key = (e: React.KeyboardEvent) => {
    if (p.count === 0) return
    const prev = p.vertical ? 'ArrowUp' : 'ArrowLeft', next = p.vertical ? 'ArrowDown' : 'ArrowRight'
    const cur = p.active ?? -1
    let to: number | null | undefined
    if (e.key === next) to = Math.min(p.count - 1, cur + 1)
    else if (e.key === prev) to = Math.max(0, cur < 0 ? 0 : cur - 1)
    else if (e.key === 'Home') to = 0
    else if (e.key === 'End') to = p.count - 1
    else if (e.key === 'Escape') to = null
    else return
    e.preventDefault()
    p.onActive(to)
  }
  const tableOnly = showTable && !p.loading && !p.empty
  return (
    <div className={cx('md-chart', p.className)} ref={ref}>
      {p.loading ? (
        <div className="md-chart__skeleton" role="status" aria-label="Caricamento del grafico" style={{ height: p.height }} />
      ) : p.empty ? (
        <EmptyState compact icon="bar_chart" title={p.emptyTitle ?? 'Nessun dato'} description={p.emptyDescription ?? 'Nel periodo scelto non ci sono dati da mostrare.'} />
      ) : (
        <>
          {tableOnly ? null : (
            <div
              className="md-chart__plot"
              style={p.autoHeight ? { minHeight: p.height } : { height: p.height }}
              role="img"
              aria-label={p.ariaLabel}
              tabIndex={0}
              onKeyDown={key}
              onBlur={() => p.onActive(null)}
              onPointerLeave={() => { p.onLeave?.() }}
            >
              {width > 0 ? p.children(width) : null}
              {p.tooltip}
            </div>
          )}
          <div className="md-sr-only" aria-live="polite">{p.announce}</div>
          <ChartTable data={p.table} />
        </>
      )}
    </div>
  )
}

/** Frase riassuntiva per lo screen reader: intervallo di categorie e minimo/massimo di ogni serie. */
export function summarize(title: string, categories: string[], series: ChartSeries[], unit: ChartUnit): string {
  if (categories.length === 0) return `${title}: nessun dato.`
  const parts = series.map(s => {
    const vs = s.values.filter((v): v is number => v != null)
    if (vs.length === 0) return `${s.name}: nessun valore`
    return `${s.name}: da ${formatChartValue(Math.min(...vs), unit)} a ${formatChartValue(Math.max(...vs), unit)}`
  })
  return `${title}. ${categories.length} voci, da ${categories[0]} a ${categories[categories.length - 1]}. ${parts.join('; ')}.`
}

export function seriesTable(caption: string, firstHead: string, categories: string[], series: ChartSeries[], unit: ChartUnit): ChartTableData {
  return {
    caption,
    head: [firstHead, ...series.map(s => s.name)],
    rows: categories.map((c, i) => ({ key: `${i}`, cells: [c, ...series.map(s => formatChartValue(s.values[i] ?? null, unit))] })),
  }
}

export function axisFormatter(ticks: number[], unit: ChartUnit): (v: number) => string {
  const integer = ticks.every(t => Number.isInteger(t))
  return v => formatChartValue(v, unit, integer ? 0 : 1)
}

export function pointerIndex(e: { clientX: number; clientY: number; currentTarget: Element }, vertical: boolean, start: number, size: number, n: number): number {
  const r = e.currentTarget.getBoundingClientRect()
  const pos = vertical ? e.clientY - r.top : e.clientX - r.left
  return Math.max(0, Math.min(n - 1, Math.floor(((pos - start) / size) * n)))
}
